import { type SupabaseClient } from "@supabase/supabase-js";

import { type Database } from "@fitness-app/supabase";

import { getLoggedUser } from "./getLoggedUser";

let viewerIdOverride: string | null = null;

/**
 * Points "whose data is on screen" at somebody other than the session user.
 *
 * Set by the client apps while a staff member previews a client's account. It deliberately lives
 * here rather than in the impersonation feature: this package cannot import from `client-shared`
 * without a cycle, and the chat list has to answer the question inside a thunk, far from React.
 *
 * Identity is untouched — `getLoggedUser` still reports the signed-in account, which is what any
 * write or permission check must keep using.
 */
export const setViewerIdOverride = (userId: string | null): void => {
  viewerIdOverride = userId;
};

/**
 * The user whose chats, counters and lists should be shown.
 *
 * Without the override this is just the signed-in account. With it, a staff member previewing a
 * client sees her conversations instead of their own book of clients.
 */
export const getViewerId = async (auth: SupabaseClient<Database>["auth"]): Promise<string> =>
  viewerIdOverride ?? (await getLoggedUser(auth)).id;

/** True while the screen belongs to someone other than the signed-in account. */
export const isViewingSomeoneElse = (): boolean => viewerIdOverride !== null;
